import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  Code,
  Pencil,
  Pin,
  PinOff,
  Plus,
  RotateCcw,
  Trash2,
  Undo2,
  X,
} from "lucide-react";

import { useAuthedApi } from "@/api/useAuthedApi";
import {
  MAX_OPEN_ITEMS,
  MAX_PROSE_CHARS,
  MEMORY_FETCH_TIMEOUT_MS,
  type ShortTermMemory,
} from "@/api/shortTermMemoryApi";
import { MAX_FACT_CHARS, type UserFact } from "@/api/factsApi";
import { RestError } from "@/api/restClient";
import { Button, Dialog, Spinner } from "@/components/ui";

/** Section block: title + optional description, then content. Mirrors the
 *  `Group` idiom in ProfilePanel so the two read as one settings surface. */
function Group({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="py-6 first:pt-0 last:pb-0 space-y-3">
      <div className="space-y-1">
        <h3 className="text-[14px] font-semibold text-ink m-0">{title}</h3>
        {description && <p className="text-[12.5px] text-ink-muted m-0">{description}</p>}
      </div>
      {children}
    </section>
  );
}

/** Every writable field shares this: an accent border and focus ring so a user
 *  can see at a glance where they can type. The read-only views deliberately
 *  carry none of it — the difference between "displayed" and "editable" should
 *  be visible without clicking. */
const FIELD =
  "w-full rounded-md border border-accent/40 bg-accent/5 px-2 py-1 text-[13.5px] " +
  "text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/25";

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-[13px] text-ink-faint m-0">{children}</p>;
}

/** Dates come off the document as YYYY-MM-DD (the parser keeps only the
 *  per-block date, not the top-level timestamp), so render exactly that
 *  rather than implying an accuracy we do not have. */
function Updated({ date }: { date: string | null }) {
  if (!date) return null;
  return <p className="text-[12px] text-ink-faint m-0">Updated {date}</p>;
}

type State =
  | { kind: "loading" }
  | { kind: "loaded"; data: ShortTermMemory }
  | { kind: "error" };

/** One editable fact. Text is rendered as plain text and edited in a plain
 *  input — never markdown, never dangerouslySetInnerHTML — because this string
 *  is model-written from user input and lands verbatim in the system prompt. */
function FactRow({
  fact,
  busy,
  editMode,
  onForget,
  onTogglePin,
  onEdit,
  onDraftChange,
}: {
  fact: UserFact;
  busy: boolean;
  /** Text editing is gated behind edit mode. Pin and remove are not: they are
   *  single-click and instantly reversible, so making users toggle a mode to
   *  fix a wrong fact would add friction to the most common action. */
  editMode: boolean;
  onForget: () => void;
  onTogglePin: () => void;
  onEdit: (text: string) => void;
  /** Reported upward because the dialog's close guard cannot see this row's
   *  local draft, and a draft the guard cannot see is the one that gets lost.
   *  Takes the id so the parent can pass one stable function to every row —
   *  a per-row closure would be a new identity each render, and the effect
   *  below would then loop. */
  onDraftChange: (id: number, dirty: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(fact.text);

  const tooLong = draft.trim().length > MAX_FACT_CHARS;
  const empty = draft.trim().length === 0;
  const dirty = editing && draft.trim() !== fact.text;
  const id = fact.id;
  useEffect(() => {
    onDraftChange(id, dirty);
    // Unmounting mid-edit (the row is removed, or edit mode closes) must clear
    // the flag, or the guard blocks a close over a draft that no longer exists.
    return () => onDraftChange(id, false);
  }, [id, dirty, onDraftChange]);

  if (editing) {
    return (
      <li data-ph-scope="fact_edit" className="flex items-start gap-2 py-1.5">
        <div className="flex-1 space-y-1">
          <input
            aria-label={`Edit fact: ${fact.text}`}
            className={FIELD}
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                // Dialog listens for Escape on the panel, so without this one
                // keypress both abandons the correction and shuts the dialog.
                e.stopPropagation();
                setEditing(false);
              }
              if (e.key === "Enter" && !empty && !tooLong) {
                onEdit(draft.trim());
                setEditing(false);
              }
            }}
          />
          {tooLong && (
            <p className="text-[12px] text-danger m-0">
              Keep it under {MAX_FACT_CHARS} characters — this is read on every turn.
            </p>
          )}
        </div>
        <button
          data-ph="save"
          aria-label="Save"
          disabled={empty || tooLong || busy}
          className="p-1 text-ink-muted hover:text-ink disabled:opacity-40"
          onClick={() => {
            onEdit(draft.trim());
            setEditing(false);
          }}
        >
          <Check size={14} />
        </button>
        <button
          data-ph="cancel"
          aria-label="Cancel"
          className="p-1 text-ink-muted hover:text-ink"
          onClick={() => {
            setDraft(fact.text);
            setEditing(false);
          }}
        >
          <X size={14} />
        </button>
      </li>
    );
  }

  return (
    <li data-ph-scope="fact" className="group flex items-start gap-2 py-1.5">
      <span className="flex-1 text-[13.5px] text-ink">{fact.text}</span>
      {fact.pinned && <span className="text-[11px] text-ink-faint mt-0.5">pinned</span>}
      <button
        // There is no pin verb: pinning adds a pin, unpinning removes it.
        data-ph={fact.pinned ? "delete_pin" : "create_pin"}
        aria-label={fact.pinned ? `Unpin: ${fact.text}` : `Pin: ${fact.text}`}
        disabled={busy}
        className="p-1 text-ink-muted hover:text-ink disabled:opacity-40"
        onClick={onTogglePin}
      >
        {fact.pinned ? <PinOff size={14} /> : <Pin size={14} />}
      </button>
      {editMode && (
        <button
          data-ph="edit"
          aria-label={`Edit: ${fact.text}`}
          disabled={busy}
          className="p-1 text-ink-muted hover:text-ink disabled:opacity-40"
          onClick={() => setEditing(true)}
        >
          <Pencil size={14} />
        </button>
      )}
      <button
        data-ph="delete"
        aria-label={`Remove: ${fact.text}`}
        disabled={busy}
        className="p-1 text-ink-muted hover:text-danger disabled:opacity-40"
        onClick={onForget}
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

/**
 * The dialog's contents. Deliberately a child of <Dialog>, which renders null
 * while closed — so this remounts on every open and refetches. That is what
 * guarantees the user always sees the current document, however many chat
 * turns have rewritten it since they last looked. No polling is needed: the
 * dialog is modal, so no turn can complete while it is on screen.
 */
function MemoryBody({ onDirtyChange }: { onDirtyChange: (dirty: boolean) => void }) {
  const { shortTermMemory, facts: factsApi } = useAuthedApi();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [showRaw, setShowRaw] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [facts, setFacts] = useState<UserFact[]>([]);
  const [removed, setRemoved] = useState<UserFact[]>([]);
  const [showRemoved, setShowRemoved] = useState(false);
  // One flag rather than per-row: these are single deliberate actions, and a
  // blanket disable stops a double-click firing two writes against the same row.
  const [busy, setBusy] = useState(false);
  // Focus lands here after a mutation removes whatever had it — see below.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [refocus, setRefocus] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [newFact, setNewFact] = useState("");
  // Activity is the one genuine draft: prose and items are edited together and
  // committed with an explicit Save. Facts commit per row, so they need no draft.
  const [activityDraft, setActivityDraft] = useState<{
    prose: string;
    items: string[];
  } | null>(null);
  // Which failure the user is looking at. "conflict" carries a specific
  // recovery instruction ("reopen, then re-apply") that is only true of a 409;
  // telling someone whose network dropped to go re-apply a draft they never had
  // sends them somewhere pointless.
  const [failure, setFailure] = useState<null | "conflict" | "error">(null);
  // Ids of fact rows with an uncommitted correction. A row owns its own draft,
  // so it has to say so — see FactRow's onDraftChange.
  const [rowDrafts, setRowDrafts] = useState<Set<number>>(() => new Set());
  const setRowDraft = useCallback((id: number, dirtyRow: boolean) => {
    setRowDrafts((prev) => {
      if (prev.has(id) === dirtyRow) return prev; // no-op keeps the effect stable
      const next = new Set(prev);
      dirtyRow ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  // Reported upward so the dialog can guard its close.
  const dirty =
    activityDraft !== null || newFact.trim() !== "" || rowDrafts.size > 0;
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const loadFacts = useCallback(async () => {
    const [active, gone] = await Promise.all([
      factsApi.list().catch(() => ({ facts: [] })),
      factsApi.listForgotten().catch(() => ({ facts: [] })),
    ]);
    setFacts(active.facts ?? []);
    setRemoved(gone.facts ?? []);
  }, [factsApi]);

  /** Every mutation refetches rather than patching local state: the server
   *  rebuilds USER.md as a side effect, so a local patch would leave the
   *  document view disagreeing with the facts that produced it. */
  const mutate = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true);
      setFailure(null);
      try {
        await fn();
        await loadFacts();
        setAttempt((n) => n + 1); // re-read USER.md, which the server just rewrote
      } catch (err) {
        // A 409 means a chat turn rewrote the document while the user was
        // editing. Surface it and keep their draft — discarding it silently is
        // the exact loss this feature exists to prevent. Anything else is an
        // ordinary failure and gets ordinary advice: the "reopen and re-apply"
        // instruction only makes sense when there is a newer version to merge.
        const conflicted = err instanceof RestError && err.status === 409;
        setFailure(conflicted ? "conflict" : "error");
      } finally {
        setBusy(false);
        setRefocus((n) => n + 1);
      }
    },
    [loadFacts],
  );

  // Removing a fact unmounts the button that had focus, dropping focus to
  // document.body — outside the dialog panel. Dialog closes on Escape via
  // onKeyDown on that panel, so without restoring focus a keyboard user is
  // trapped in the dialog after a removal. Done in an effect rather than
  // inline so it runs after the re-render that removed the row.
  useEffect(() => {
    if (refocus > 0) bodyRef.current?.focus();
  }, [refocus]);

  useEffect(() => {
    const controller = new AbortController();
    // Both cancellations matter: unmount (user closed the dialog) and a
    // deadline (a request that hangs would otherwise spin forever, which reads
    // as "still working" when nothing is coming).
    const timer = setTimeout(() => controller.abort(), MEMORY_FETCH_TIMEOUT_MS);
    let cancelled = false;

    // Only blank to a spinner when there is nothing to show yet. A refresh
    // after an edit keeps the current content on screen: flashing the whole
    // dialog to a spinner on every pin or removal is jarring, and it unmounts
    // the body — taking focus with it, which breaks Escape-to-close.
    setState((prev) => (prev.kind === "loaded" ? prev : { kind: "loading" }));
    shortTermMemory
      .get(controller.signal)
      .then((data) => {
        if (!cancelled) setState({ kind: "loaded", data });
      })
      .catch(() => {
        // Backend unreachable, 5xx, or timed out — all converge here, on the
        // same state as an "unavailable" payload, so a failure can never be
        // presented as an empty memory.
        if (!cancelled) setState({ kind: "error" });
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [shortTermMemory, attempt]);

  // Facts load once per open. Deliberately not folded into the effect above:
  // that one re-runs on `attempt` to re-read USER.md after a write, and
  // refetching facts there too would race the mutation's own refresh.
  useEffect(() => {
    void loadFacts();
  }, [loadFacts]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  if (state.kind === "loading") {
    return (
      // Spinner already carries role="status"; don't nest another one.
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  // Not a fault: this workspace has no memory store provisioned, so say that
  // plainly and offer no retry — retrying could never change the outcome.
  if (state.kind === "loaded" && state.data.status === "not_configured") {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="text-[13.5px] text-ink m-0">Memory isn&rsquo;t set up for this workspace yet.</p>
        <p className="text-[12.5px] text-ink-muted m-0 max-w-sm">
          Once it is, anything Artie picks up about how you work will show here.
        </p>
      </div>
    );
  }

  if (state.kind === "error" || state.data.status === "unavailable") {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <p className="text-[13.5px] text-ink m-0">Couldn&rsquo;t load your memory.</p>
        <p className="text-[12.5px] text-ink-muted m-0 max-w-sm">
          This is a loading problem, not an empty memory — anything Artie has
          stored is still there.
        </p>
        <Button variant="secondary" data-ph="refresh" onClick={retry}>
          <RotateCcw size={14} /> Retry
        </Button>
      </div>
    );
  }

  const { profile, recent_activity: activity, raw, raw_truncated, version } = state.data;
  const draft = activityDraft ?? { prose: activity.prose, items: activity.open_items };

  const exitEditMode = () => {
    setEditMode(false);
    setActivityDraft(null);
    setNewFact("");
    setFailure(null);
  };

  return (
    <div ref={bodyRef} tabIndex={-1} className="px-6 py-5 divide-y divide-hairline outline-none">
      <div className="flex items-center justify-between pb-4">
        <p className="text-[12.5px] text-ink-muted m-0">
          {editMode
            ? "Changes to your profile save as you make them."
            : "What Artie remembers about you."}
        </p>
        {editMode ? (
          <Button variant="secondary" data-ph="close_edit" onClick={exitEditMode}>
            Done
          </Button>
        ) : (
          <Button variant="secondary" data-ph="edit" onClick={() => setEditMode(true)}>
            <Pencil size={14} /> Edit
          </Button>
        )}
      </div>

      {failure && (
        <div className="pb-4">
          <p className="text-[12.5px] text-danger m-0">
            {failure === "conflict"
              ? "Your memory changed while you were editing — your text is still here. Close and reopen to see the newest version, then re-apply it."
              : "Couldn’t save that change — your text is still here. Try again."}
          </p>
        </div>
      )}

      <Group
        // Deliberately NOT "Profile". The document has a `## Profile` block,
        // but that carries only preference/default/context facts — this list
        // shows every fact, recurring_intent included. Sharing the name made
        // the raw view look stale to anyone comparing the two, when the rows
        // missing from it are excluded by category rather than lost.
        title="What Artie remembers"
        // Artie rewrites this list as it keeps learning, so a fact can change
        // or drop out on its own. Pinning is how you say a particular one
        // shouldn't — worth spelling out, because the icon alone reads as a
        // bookmark rather than as the thing that makes a fact permanent.
        description="Durable things Artie has picked up about how you work. Pin anything that should not change — pinned facts stay exactly as written and are never dropped as Artie learns more."
      >
        <Updated date={profile.updated} />
        {/* Rendered from the facts themselves rather than the USER.md bullets
            they produce: facts carry the ids these controls act on, and they
            are the source the document is re-rendered from after any edit. */}
        {facts.length === 0 ? (
          <Empty>Nothing here yet — this fills in as you chat.</Empty>
        ) : (
          <ul className="m-0 list-none pl-0 divide-y divide-hairline/50">
            {facts.map((f) => (
              <FactRow
                key={f.id}
                fact={f}
                busy={busy}
                editMode={editMode}
                onForget={() => void mutate(() => factsApi.forget(f.id))}
                onTogglePin={() => void mutate(() => factsApi.setPinned(f.id, !f.pinned))}
                onEdit={(text) => void mutate(() => factsApi.edit(f.id, text))}
                onDraftChange={setRowDraft}
              />
            ))}
          </ul>
        )}

        {editMode && (
          <form
            data-ph-scope="facts"
            className="flex items-center gap-2 pt-2"
            onSubmit={(e) => {
              e.preventDefault();
              const text = newFact.trim();
              if (!text) return;
              setNewFact("");
              void mutate(() => factsApi.create(text));
            }}
          >
            <input
              aria-label="Add a fact"
              placeholder="Tell Artie something about how you work"
              className={`flex-1 ${FIELD}`}
              value={newFact}
              maxLength={MAX_FACT_CHARS}
              onChange={(e) => setNewFact(e.target.value)}
            />
            <button
              type="submit"
              data-ph="create"
              aria-label="Add fact"
              disabled={busy || !newFact.trim()}
              className="p-1 text-ink-muted hover:text-ink disabled:opacity-40"
            >
              <Plus size={16} />
            </button>
          </form>
        )}
      </Group>

      {removed.length > 0 && (
        <Group title="Removed">
          {/* Kept out of Profile on purpose: these no longer reach the prompt,
              so listing them alongside active facts would misrepresent what
              Artie knows. Collapsed by default because people remove things
              they would rather not keep re-reading. */}
          <button
            data-ph={showRemoved ? "removed-collapse" : "removed-expand"}
            className="text-[12.5px] text-ink-muted hover:text-ink"
            aria-expanded={showRemoved}
            onClick={() => setShowRemoved((v) => !v)}
          >
            {showRemoved ? "Hide" : "Show"} removed ({removed.length})
          </button>
          {showRemoved && (
            <ul className="m-0 list-none pl-0 divide-y divide-hairline/50">
              {removed.map((f) => (
                <li key={f.id} className="flex items-start gap-2 py-1.5">
                  <span className="flex-1 text-[13.5px] text-ink-faint line-through">
                    {f.text}
                  </span>
                  <button
                    // Restoring undoes a removal; there is no restore verb.
                    data-ph="removed-cancel_delete"
                    aria-label={`Restore: ${f.text}`}
                    disabled={busy}
                    className="p-1 text-ink-muted hover:text-ink disabled:opacity-40"
                    onClick={() => void mutate(() => factsApi.restore(f.id))}
                  >
                    <Undo2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Group>
      )}

      <Group title="Recent activity" description="A rolling summary of what you have been doing.">
        <Updated date={activity.updated} />
        {editMode ? (
          <div data-ph-scope="activity" className="space-y-2">
            <textarea
              aria-label="Recent activity summary"
              rows={3}
              maxLength={MAX_PROSE_CHARS}
              className={FIELD}
              value={draft.prose}
              onChange={(e) =>
                setActivityDraft({ prose: e.target.value, items: draft.items })
              }
            />
            <p className="text-[12.5px] text-ink-muted m-0">
              Outstanding items (up to {MAX_OPEN_ITEMS}) — things left unfinished,
              so Artie knows about them. It won&rsquo;t act on them on its own.
            </p>
            {draft.items.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  aria-label={`Open item ${i + 1}`}
                  className={`flex-1 ${FIELD}`}
                  value={item}
                  onChange={(e) => {
                    const items = [...draft.items];
                    items[i] = e.target.value;
                    setActivityDraft({ prose: draft.prose, items });
                  }}
                />
                <button
                  data-ph="delete_item"
                  aria-label={`Remove open item ${i + 1}`}
                  className="p-1 text-ink-muted hover:text-danger"
                  onClick={() =>
                    setActivityDraft({
                      prose: draft.prose,
                      items: draft.items.filter((_, j) => j !== i),
                    })
                  }
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {draft.items.length < MAX_OPEN_ITEMS && (
              <button
                data-ph="create_item"
                aria-label="Add open item"
                className="text-[12.5px] text-ink-muted hover:text-ink"
                onClick={() =>
                  setActivityDraft({ prose: draft.prose, items: [...draft.items, ""] })
                }
              >
                + Add open item
              </button>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Button
                data-ph="save"
                onClick={() =>
                  void mutate(async () => {
                    await shortTermMemory.saveActivity({
                      prose: draft.prose,
                      open_items: draft.items.filter((i) => i.trim()),
                      version,
                    });
                    setActivityDraft(null);
                  })
                }
                disabled={busy}
              >
                <Check size={14} /> Save activity
              </Button>
              <Button variant="secondary" data-ph="cancel" onClick={() => setActivityDraft(null)} disabled={busy}>
                Discard
              </Button>
            </div>
            {/* Only offered once the block is actually user-owned, otherwise it
                would imply Artie had stopped managing something it still owns. */}
            {activity.user_edited && (
              <button
                // Drops the user's override so Artie owns the block again.
                data-ph="delete_override"
                className="text-[12.5px] text-ink-muted hover:text-ink"
                onClick={() =>
                  void mutate(async () => {
                    await shortTermMemory.clearActivityEdit({
                      prose: draft.prose,
                      open_items: draft.items.filter((i) => i.trim()),
                      version,
                    });
                    setActivityDraft(null);
                  })
                }
              >
                Let Artie manage this again
              </button>
            )}
          </div>
        ) : !activity.prose && activity.open_items.length === 0 ? (
          <Empty>Nothing here yet — this fills in as you chat.</Empty>
        ) : (
          <>
            {activity.prose && <p className="text-[13.5px] text-ink m-0">{activity.prose}</p>}
            {activity.open_items.length > 0 && (
              <>
                {/* The file marks these "- Open:"; without a label they read as
                    more summary prose rather than outstanding items. */}
                <p className="text-[12.5px] text-ink-muted m-0">Outstanding items</p>
                <ul className="m-0 list-disc pl-5 space-y-1.5">
                  {activity.open_items.map((item) => (
                    <li key={item} className="text-[13.5px] text-ink">
                      {item}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Group>

      <Group title="The file itself">
        <p className="text-[12.5px] text-ink-muted m-0">
          This is your short-term memory — a single Markdown document Artie
          reads at the start of every turn. Longer-term knowledge is kept
          separately.
        </p>
        {raw === null ? (
          <Empty>No document has been written yet.</Empty>
        ) : (
          <>
            <Button
              variant="secondary"
              data-ph={showRaw ? "raw-collapse" : "raw-expand"}
              onClick={() => setShowRaw((v) => !v)}
            >
              <Code size={14} /> {showRaw ? "Hide raw" : "View raw"}
            </Button>
            {showRaw && (
              <>
                {raw_truncated && (
                  <p className="text-[12px] text-ink-faint m-0">
                    Truncated — this document is unusually large, so only the
                    beginning is shown.
                  </p>
                )}
                <pre className="mt-2 max-h-80 overflow-auto rounded-[10px] bg-surface border border-edge p-3 text-[12px] leading-relaxed text-ink-secondary whitespace-pre-wrap break-words">
                  {raw}
                </pre>
              </>
            )}
          </>
        )}
      </Group>
    </div>
  );
}

/** The user's short-term memory (USER.md), opened from the account menu.
 *  Facts can be pinned, removed and restored directly; text editing and adding
 *  live behind an Edit toggle. The raw document stays read-only — the client
 *  submits structured fields and the server re-renders it. */
export function MemoryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [dirty, setDirty] = useState(false);
  const [warned, setWarned] = useState(false);

  useEffect(() => {
    if (!open) setWarned(false);
  }, [open]);

  // Escape and the backdrop close instantly, which is right for a read — but
  // edit mode turns this into a draft that takes minutes to write, and losing
  // it to a stray keypress is the exact failure this feature exists to remove.
  // One warning, then the second attempt goes through: a guard the user cannot
  // get past would be worse than the loss it prevents.
  const handleClose = () => {
    if (dirty && !warned) {
      setWarned(true);
      return;
    }
    onClose();
  };

  return (
    // Opened from the sidebar's account menu and portaled out of any page, so
    // it declares its fixed global-chrome path rather than an area.
    <Dialog open={open} onClose={handleClose} title="Memory" size="lg" scope="sp-memory">
      {warned && dirty && (
        <p
          role="alert"
          className="mx-6 mt-1 mb-0 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[12.5px] text-ink"
        >
          You have unsaved changes. Save them, or close again to discard.
        </p>
      )}
      <MemoryBody onDirtyChange={setDirty} />
    </Dialog>
  );
}
